// Confidence calibration — the vocabulary, shared by the web app and the phone.
//
// Before feedback on a PRACTICE question a student may tap how sure they are:
// Certain, Unsure or Guess. It is optional (no tap = no rating, never blocks
// Submit) and it appears on practice surfaces only — never on the SAT runner, a
// Checkpoint, a Benchmark, a placement check or Rapid Fire.
//
// Rating × correctness gives four cells:
//
//                 correct              wrong
//   CERTAIN       mastered             confident misconception  <- the one that matters most
//   UNSURE/GUESS  lucky / fragile      known gap
//
// Pure, framework-free (see index.ts). The web's server rules that act on a
// rating (mastery credit, review scheduling, events) live in
// src/lib/practice/confidence.ts; this file only says what the values ARE.

export const CONFIDENCE_RATINGS = ['CERTAIN', 'UNSURE', 'GUESS'] as const
export type ConfidenceRating = (typeof CONFIDENCE_RATINGS)[number]

/** What the student taps. Short on purpose: the control must cost one glance. */
export const CONFIDENCE_LABEL: Record<ConfidenceRating, string> = {
  CERTAIN: 'Certain',
  UNSURE: 'Unsure',
  GUESS: 'Guess',
}

/**
 * Lenient parse of a rating from a request body or stored JSON. Anything that
 * isn't exactly one of the three values is "not rated" (null) — a bad value
 * must never fail the answer it rode in on.
 */
export function parseConfidence(raw: unknown): ConfidenceRating | null {
  return typeof raw === 'string' && (CONFIDENCE_RATINGS as readonly string[]).includes(raw)
    ? (raw as ConfidenceRating)
    : null
}

export type CalibrationCell =
  /** Certain and right. */
  | 'mastered'
  /** Certain and wrong — a confident misconception. */
  | 'misconception'
  /** Unsure or Guess, and right — not truly known yet. */
  | 'fragile'
  /** Unsure or Guess, and wrong — a gap the student already knew about. */
  | 'gap'

/** The cell a rated answer lands in; null when it wasn't rated. */
export function calibrationCell(
  confidence: ConfidenceRating | null | undefined,
  correct: boolean,
): CalibrationCell | null {
  if (!confidence) return null
  if (confidence === 'CERTAIN') return correct ? 'mastered' : 'misconception'
  return correct ? 'fragile' : 'gap'
}

/**
 * The one-line note shown under the result, for the two cells worth a word:
 * a confident miss, and a correct guess. Everything else gets nothing — the
 * result panel already says right or wrong, and a line on every answer would
 * be noise. Short and non-judgmental: being sure and wrong is normal and the
 * most useful thing a student can find out.
 */
export function confidenceFeedbackLine(
  confidence: ConfidenceRating | null | undefined,
  correct: boolean,
): string | null {
  if (confidence === 'CERTAIN' && !correct) {
    return "You were sure on this one — that's the most useful kind of miss to find. Worth a closer look at where it slipped."
  }
  if (confidence === 'GUESS' && correct) {
    return "Right — but you guessed, so it'll come back in your review to make sure it sticks."
  }
  return null
}
